import {CommonModule} from "@angular/common";
import {booleanAttribute, Component, EventEmitter, Input, OnChanges, Optional, Output, ChangeDetectionStrategy} from "@angular/core";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";

import {MaterialModule} from "framework/material";
import {Lookup} from "framework/services/utils.service";
import {IconComponent} from "framework/ui/icons/icon.component";

@Component({
    selector: "cs-button",
    templateUrl: "./button.component.html",
    styleUrls: ["./button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        IconComponent,
        MaterialModule
    ]
})
export class ButtonComponent implements OnChanges
{
    @Input() label: string;
    @Input() csIcon: string;
    @Input() icon: string;
    @Input() menuTrigger: MatMenu;
    @Input() buttonType: ButtonType                           = "button";
    @Input() size: ButtonSize                                 = "medium";
    @Input() buttonStyle: ButtonStyle                         = "primary";
    @Input({transform: booleanAttribute}) disabled: boolean;
    @Input() iconPosition: ButtonIconPosition                 = "left";
    @Input() overrideStyles: Lookup<string>;
    @Input({transform: booleanAttribute}) orange: boolean     = false;
    @Input({transform: booleanAttribute}) useLoader: boolean  = false;
    @Input({transform: booleanAttribute}) showLoader: boolean = false;

    @Output() buttonClick      = new EventEmitter<Event>();
    @Output() showLoaderChange = new EventEmitter<boolean>();

    buttonClasses: string;

    constructor(@Optional() private m_trigger: MatMenuTrigger) {}

    get loaderSvgSrc(): string
    {
        if (this.buttonStyle === "primary")
        {
            return "assets/loaders/loader-icon-white.svg";
        }
        else
        {
            return "assets/loaders/loader-icon-primary.svg";
        }
    }

    ngOnChanges()
    {
        this.buttonClasses = this.generateButtonClasses();
    }

    private generateButtonClasses(): string
    {
        let classes: string[] = [];

        switch (this.iconPosition)
        {
            case "left":
                classes.push("cs-btn-with-left-icon");
                break;

            case "right":
                classes.push("cs-btn-with-right-icon");
                break;
        }

        switch (this.size)
        {
            case "large":
                classes.push("cs-btn-lg");
                break;

            case "medium":
                classes.push("cs-btn-med");
                break;

            case "small":
                classes.push("cs-btn-sm");
                break;
        }

        switch (this.buttonStyle)
        {
            case "primary":
                classes.push("cs-btn-primary");
                break;

            case "secondary":
                classes.push("cs-btn-secondary");
                break;

            case "tertiary":
                classes.push("cs-btn-tertiary");
                break;
        }

        if (this.useLoader && this.showLoader)
        {
            classes.push("cs-btn-readonly");
        }

        if (this.orange)
        {
            classes.push("cs-btn-orange");
        }

        return classes.join(" ");
    }

    onClick(event: Event)
    {
        if (this.useLoader)
        {
            if (this.showLoader)
            {
                event.stopPropagation();
                return;
            }
            else
            {
                this.showLoader = true;
                this.showLoaderChange.emit(this.showLoader);
            }
        }

        if (this.buttonClick.observed && !this.m_trigger)
        {
            event.stopPropagation();
        }

        this.buttonClick.emit(event);
    }
}

export type ButtonStyle = "primary" | "secondary" | "tertiary";
export type ButtonSize = "large" | "medium" | "small";
export type ButtonIconPosition = "left" | "right";
export type ButtonType = "button" | "submit";
